import { useDisclosure } from "@caspeco/casper-ui-library";
import { useIsMutating } from "@tanstack/react-query";
import { useRefreshBooking } from "api/api-hooks/use-refresh-booking";
import { useWebBooking } from "api/api-hooks/use-web-booking";
import ConfirmationDialog from "components/confirm-dialog";
import { useBookingId } from "hooks/use-booking-id";
import { useBookingStateContext } from "hooks/use-booking-state";
import { error as logError, warn } from "logger/logger";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Routes } from "routes";
import { BookingStatus } from "types/booking-status";
import SingleButtonDialog from "./single-button-dialog";

type LocationState = {
    skipValidation?: boolean;
};

export const BookingValidator = () => {
    const { t } = useTranslation();
    const bookingId = useBookingId();
    const { data: booking, isLoading, refetch } = useWebBooking();
    const refreshBooking = useRefreshBooking();
    const location = useLocation();
    const { system, unitId } = useParams();
    const [showExpiredDialog, setShowExpiredDialog] = useState(false);
    const { isOpen, onClose, onOpen } = useDisclosure();
    const navigate = useNavigate();
    const { isActivityBooking } = useBookingStateContext();

    const validationState = useRef({
        hasAttemptedRefresh: false,
        isValidating: false,
        lastBookingStatus: undefined as BookingStatus | undefined,
        lastBookingId: undefined as string | null | undefined,
    });

    const nonInProgress = booking?.isCompleted();

    const skipValidation = Boolean((location.state as LocationState)?.skipValidation);

    // Check if a finalization mutation is in progress to avoid validation errors during booking finalization
    const isFinalizing = useIsMutating({ mutationKey: ["finalize-booking"] }) > 0;

    // Detect if booking just transitioned to finalized status (avoid validation during finalization)
    const currentStatus = booking?.status;
    const previousStatus = validationState.current.lastBookingStatus;

    // Reset status tracking when booking changes to avoid cross-booking contamination
    if (validationState.current.lastBookingId !== bookingId) {
        validationState.current.lastBookingStatus = undefined;
        validationState.current.lastBookingId = bookingId;
    }

    const justFinalized =
        previousStatus !== undefined &&
        (previousStatus === BookingStatus.BookingInProgress ||
            previousStatus === BookingStatus.Inquiry ||
            previousStatus === BookingStatus.WaitList) &&
        booking?.isFinalized();

    // Update the last known status
    useEffect(() => {
        validationState.current.lastBookingStatus = currentStatus;
    }, [currentStatus, bookingId]);

    useEffect(() => {
        // Skip validation if navigating from finalize booking to prevent flash of error modal
        if (skipValidation) return;

        // Skip validation while finalization mutation is in progress to prevent error logging
        if (isFinalizing) {
            return;
        }

        // Skip validation if booking just transitioned to finalized (during finalization flow)
        if (justFinalized) {
            return;
        }

        if (!bookingId || isLoading || !booking || validationState.current.isValidating) return;

        const validateBooking = async () => {
            validationState.current.isValidating = true;

            try {
                // Expired booking that hasn't been refreshed yet - try to refresh it
                if (
                    booking.status === BookingStatus.Expired &&
                    !validationState.current.hasAttemptedRefresh
                ) {
                    warn("Booking expired, attempting to refresh...");
                    validationState.current.hasAttemptedRefresh = true;

                    try {
                        await refreshBooking.mutateAsync();
                        const { data: refreshedBooking } = await refetch();

                        // Show dialog if refresh didn't work
                        if (refreshedBooking?.status !== BookingStatus.BookingInProgress) {
                            setShowExpiredDialog(true);
                        }
                    } catch (error) {
                        logError("Error refreshing booking:", error);
                        setShowExpiredDialog(true);
                    }
                }
                // non-in-progress booking should not refresh
                else if (
                    booking.status !== BookingStatus.BookingInProgress &&
                    booking.status !== BookingStatus.Inquiry &&
                    booking.status !== BookingStatus.WaitList &&
                    booking.status !== BookingStatus.Web
                ) {
                    setShowExpiredDialog(true);
                } else if (nonInProgress) {
                    onOpen();
                }
            } finally {
                validationState.current.isValidating = false;
            }
        };

        void validateBooking();
    }, [
        bookingId,
        booking,
        isLoading,
        refreshBooking,
        refetch,
        onOpen,
        nonInProgress,
        skipValidation,
        isFinalizing,
        justFinalized,
    ]);

    const handleDialogClose = () => {
        setShowExpiredDialog(false);

        let redirectPath = `/${system}/${unitId}`;
        if (location.pathname.includes(Routes.ACTIVITY_BOOKING)) {
            redirectPath += `/${Routes.ACTIVITY_BOOKING}`;
        }

        void navigate(redirectPath, { state: { intercept: false } });
    };

    const startOver = () => {
        let bookMoreUrl = `/${system}/${booking?.unitId}`;
        if (isActivityBooking) bookMoreUrl += "/activity-booking";
        void navigate(bookMoreUrl, { state: { intercept: false } });
    };
    const goToBooking = () => void navigate(`/booking/${system}/${bookingId}`);

    const getBookingType = () => {
        if (booking?.isInquiry()) return "request";
        if (booking?.isWaitList()) return "waitList";
        return "booking";
    };
    const bookingType = getBookingType();
    return (
        <>
            <SingleButtonDialog
                isOpen={showExpiredDialog}
                title={t("validation.sessionExpired.title")}
                message={t("validation.sessionExpired.message")}
                buttonText={t("validation.ok")}
                onAction={handleDialogClose}
                onClose={handleDialogClose}
            />
            <ConfirmationDialog
                isOpen={isOpen}
                title={t(`validation.alreadyFinalized.${bookingType}.title`)}
                message={t(`validation.alreadyFinalized.${bookingType}.message`)}
                primaryAction={goToBooking}
                secondaryAction={startOver}
                onClose={onClose}
                primaryButtonText={t(`validation.alreadyFinalized.${bookingType}.primaryBtn`)}
                secondaryButtonText={t(`validation.alreadyFinalized.${bookingType}.secondaryBtn`)}
            />
        </>
    );
};
